import React, { useState } from 'react';
import cn from 'classnames';
import { Select as SharedSelect } from '../../../ComponnentsUI/common/Select';
import {
  DicomSyntaxConfig,
  AbstractSyntaxConfig,
  defaultSyntaxConfig,
} from '../types/interfaces';

export const generateAETitle = (prefix: string = 'AE_'): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const maxLength = 8;
  const suffixLength = Math.max(1, maxLength - prefix.length);

  let suffix = '';
  for (let i = 0; i < suffixLength; i++) {
    suffix += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return prefix + suffix;
};

export const generateAEName = (prefix: string = 'DEEPECHO_'): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const defaultName = `${year}${month}${day}-${hours}${minutes}`;
  return `${prefix}${defaultName}`;
};

export interface SyntaxTransformOptions {

  role?: 'SCP' | 'SCU';
  cFindWorklist?: boolean;
  cFindStudy?: boolean;
}

const MWL_FIND_SOP_CLASS = '1.2.840.10008.5.1.4.31';
const STUDY_ROOT_FIND_SOP_CLASS = '1.2.840.10008.5.1.4.1.2.2.1';
const PATIENT_ROOT_FIND_SOP_CLASS = '1.2.840.10008.5.1.4.1.2.1.1';


const QUERY_TRANSFER_SYNTAXES = '1.2.840.10008.1.2.1,1.2.840.10008.1.2';

export const transformSyntaxConfigToAPI = (
  syntaxConfig: DicomSyntaxConfig,
  options: SyntaxTransformOptions = {}
) => {
  const role = options.role ?? 'SCP';
  const transferCapabilities = [];
  const services = [];

  const getTransferSyntaxes = (
    config: AbstractSyntaxConfig | Partial<AbstractSyntaxConfig>
  ) => {
    const syntaxes = [];
    const mapping = {
      implicitVrLittleEndian: '1.2.840.10008.1.2',
      explicitVrLittleEndian: '1.2.840.10008.1.2.1',
      explicitVrBigEndian: '1.2.840.10008.1.2.2',
      jpegBaselineProcess1: '1.2.840.10008.1.2.4.50',
      jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
        '1.2.840.10008.1.2.4.70',
      jpegLsLosslessImageCompression: '1.2.840.10008.1.2.4.80',
      jpegLsLossyNearLosslessImageCompression: '1.2.840.10008.1.2.4.81',
      jpeg2000ImageCompressionLosslessOnly: '1.2.840.10008.1.2.4.90',
      jpeg2000ImageCompression: '1.2.840.10008.1.2.4.91',
      mpeg4AvcH264HighProfile: '1.2.840.10008.1.2.4.102',
    };

    Object.entries(mapping).forEach(([key, uid]) => {
      if (config[key as keyof typeof config] === 'accepted') {
        syntaxes.push(uid);
      }
    });

    return syntaxes.join(',');
  };

  const verificationTransferSyntaxes = getTransferSyntaxes(
    syntaxConfig.verificationSopClass
  );
  if (verificationTransferSyntaxes) {
    transferCapabilities.push({
      sopClass: '1.2.840.10008.1.1',
      role,
      transferSyntaxes: verificationTransferSyntaxes,
      commonName: 'Verification SOP Class',
      enabled: true,
      priority: 0,
    });
  }

  const secondaryCaptureTransferSyntaxes = getTransferSyntaxes(
    syntaxConfig.secondaryCaptureImageStorage
  );
  if (secondaryCaptureTransferSyntaxes) {
    transferCapabilities.push({
      sopClass: '1.2.840.10008.5.1.4.1.1.7',
      role,
      transferSyntaxes: secondaryCaptureTransferSyntaxes,
      commonName: 'Secondary Capture Image Storage',
      enabled: true,
      priority: 0,
    });
  }

  const ultrasoundTransferSyntaxes = getTransferSyntaxes(
    syntaxConfig.ultrasoundImageStorage
  );
  if (ultrasoundTransferSyntaxes) {
    transferCapabilities.push({
      sopClass: '1.2.840.10008.5.1.4.1.1.6.1',
      role,
      transferSyntaxes: ultrasoundTransferSyntaxes,
      commonName: 'Ultrasound Image Storage',
      enabled: true,
      priority: 0,
    });
  }

  const ultrasoundMultiFrameTransferSyntaxes = getTransferSyntaxes(
    syntaxConfig.ultrasoundMultiFrameImageStorage
  );
  if (ultrasoundMultiFrameTransferSyntaxes) {
    transferCapabilities.push({
      sopClass: '1.2.840.10008.5.1.4.1.1.3.1',
      role,
      transferSyntaxes: ultrasoundMultiFrameTransferSyntaxes,
      commonName: 'Ultrasound Multi-frame Image Storage',
      enabled: true,
      priority: 0,
    });
  }

  services.push({
    serviceType: 'C_ECHO',
    description: 'DICOM Verification Service with auto-generated TLS',
    enabled: true,
    priority: 1,
    sopClasses: [
      {
        sopClassUid: '1.2.840.10008.1.1',
        sopClassName: 'Verification SOP Class',
      },
    ],
  });

  const storeSOPClasses = [
    {
      sopClassUid: '1.2.840.10008.5.1.4.1.1.7',
      sopClassName: 'Secondary Capture Image Storage',
    },
    {
      sopClassUid: '1.2.840.10008.5.1.4.1.1.6.1',
      sopClassName: 'Ultrasound Image Storage',
    },
    {
      sopClassUid: '1.2.840.10008.5.1.4.1.1.3.1',
      sopClassName: 'Ultrasound Multi-frame Image Storage',
    },
  ];

  if (storeSOPClasses.length > 0) {
    services.push({
      serviceType: 'C_STORE',
      description: 'DICOM Storage Service with auto-generated TLS',
      enabled: true,
      priority: 1,
      sopClasses: storeSOPClasses,
    });
  }


  const findSopClasses: { sopClassUid: string; sopClassName: string }[] = [];

  if (options.cFindWorklist) {
    transferCapabilities.push({
      sopClass: MWL_FIND_SOP_CLASS,
      role: 'SCU',
      transferSyntaxes: QUERY_TRANSFER_SYNTAXES,
      commonName: 'Modality Worklist Information Model - FIND',
      enabled: true,
      priority: 0,
    });
    findSopClasses.push({
      sopClassUid: MWL_FIND_SOP_CLASS,
      sopClassName: 'Modality Worklist Information Model - FIND',
    });
  }

  if (options.cFindStudy) {
    transferCapabilities.push({
      sopClass: STUDY_ROOT_FIND_SOP_CLASS,
      role: 'SCU',
      transferSyntaxes: QUERY_TRANSFER_SYNTAXES,
      commonName: 'Study Root Query/Retrieve Information Model - FIND',
      enabled: true,
      priority: 0,
    });

    transferCapabilities.push({
      sopClass: PATIENT_ROOT_FIND_SOP_CLASS,
      role: 'SCU',
      transferSyntaxes: QUERY_TRANSFER_SYNTAXES,
      commonName: 'Patient Root Query/Retrieve Information Model - FIND',
      enabled: true,
      priority: 0,
    });
    findSopClasses.push(
      {
        sopClassUid: STUDY_ROOT_FIND_SOP_CLASS,
        sopClassName: 'Study Root Query/Retrieve Information Model - FIND',
      },
      {
        sopClassUid: PATIENT_ROOT_FIND_SOP_CLASS,
        sopClassName: 'Patient Root Query/Retrieve Information Model - FIND',
      }
    );
  }

  if (findSopClasses.length > 0) {
    services.push({
      serviceType: 'C_FIND',
      description: 'Outbound C-FIND (worklist and query/retrieve)',
      enabled: true,
      priority: 1,
      sopClasses: findSopClasses,
    });
  }

  return { transferCapabilities, services };
};

export const parseDefaultManufacturers = (defaultConfiguration: any) => {
  if (!defaultConfiguration?.defaultManufacturers) {
    return {};
  }

  const manufacturerModelData: Record<
    string,
    { label: string; value: string }[]
  > = {};

  defaultConfiguration.defaultManufacturers.forEach((manufacturer: any) => {
    manufacturerModelData[manufacturer.manufacturerName] =
      manufacturer.models.map((model: any) => ({
        label: model.modelName,
        value: model.modelName,
      }));
  });

  return manufacturerModelData;
};

export const parseDefaultTransferSyntaxes = (
  defaultConfiguration: any
): DicomSyntaxConfig => {
  if (!defaultConfiguration?.defaultTransferSyntaxes) {
    return defaultSyntaxConfig;
  }

  const syntaxConfig: DicomSyntaxConfig = {
    secondaryCaptureImageStorage: {
      explicitVrBigEndian: 'rejected',
      explicitVrLittleEndian: 'rejected',
      implicitVrLittleEndian: 'rejected',
      jpeg2000ImageCompression: 'rejected',
      jpeg2000ImageCompressionLosslessOnly: 'rejected',
      jpegBaselineProcess1: 'rejected',
      jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
        'rejected',
      jpegLsLosslessImageCompression: 'rejected',
      jpegLsLossyNearLosslessImageCompression: 'rejected',
      mpeg4AvcH264HighProfile: 'rejected',
    },
    ultrasoundImageStorage: {
      explicitVrBigEndian: 'rejected',
      explicitVrLittleEndian: 'rejected',
      implicitVrLittleEndian: 'rejected',
      jpeg2000ImageCompression: 'rejected',
      jpeg2000ImageCompressionLosslessOnly: 'rejected',
      jpegBaselineProcess1: 'rejected',
      jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
        'rejected',
      jpegLsLosslessImageCompression: 'rejected',
      jpegLsLossyNearLosslessImageCompression: 'rejected',
      mpeg4AvcH264HighProfile: 'rejected',
    },
    ultrasoundMultiFrameImageStorage: {
      explicitVrBigEndian: 'rejected',
      explicitVrLittleEndian: 'rejected',
      implicitVrLittleEndian: 'rejected',
      jpeg2000ImageCompression: 'rejected',
      jpeg2000ImageCompressionLosslessOnly: 'rejected',
      jpegBaselineProcess1: 'rejected',
      jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
        'rejected',
      jpegLsLosslessImageCompression: 'rejected',
      jpegLsLossyNearLosslessImageCompression: 'rejected',
      mpeg4AvcH264HighProfile: 'rejected',
    },
    verificationSopClass: {
      explicitVrBigEndian: 'rejected',
      explicitVrLittleEndian: 'rejected',
      implicitVrLittleEndian: 'rejected',
    },
  };

  // UID to property mapping for transfer syntaxes
  const transferSyntaxMapping: Record<string, keyof AbstractSyntaxConfig> = {
    '1.2.840.10008.1.2': 'implicitVrLittleEndian',
    '1.2.840.10008.1.2.1': 'explicitVrLittleEndian',
    '1.2.840.10008.1.2.2': 'explicitVrBigEndian',
    '1.2.840.10008.1.2.4.50': 'jpegBaselineProcess1',
    '1.2.840.10008.1.2.4.70':
      'jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1',
    '1.2.840.10008.1.2.4.80': 'jpegLsLosslessImageCompression',
    '1.2.840.10008.1.2.4.81': 'jpegLsLossyNearLosslessImageCompression',
    '1.2.840.10008.1.2.4.90': 'jpeg2000ImageCompressionLosslessOnly',
    '1.2.840.10008.1.2.4.91': 'jpeg2000ImageCompression',
    '1.2.840.10008.1.2.4.102': 'mpeg4AvcH264HighProfile',
  };

  // Abstract syntax UID to config key mapping
  const abstractSyntaxMapping: Record<string, keyof DicomSyntaxConfig> = {
    '1.2.840.10008.1.1': 'verificationSopClass',
    '1.2.840.10008.5.1.4.1.1.7': 'secondaryCaptureImageStorage',
    '1.2.840.10008.5.1.4.1.1.6.1': 'ultrasoundImageStorage',
    '1.2.840.10008.5.1.4.1.1.3.1': 'ultrasoundMultiFrameImageStorage',
  };

  defaultConfiguration.defaultTransferSyntaxes.forEach((defaultSyntax: any) => {
    const abstractSyntaxKey =
      abstractSyntaxMapping[defaultSyntax.abstractSyntax];
    if (!abstractSyntaxKey) {
      return;
    }

    try {
      const acceptedTransferSyntaxes = JSON.parse(
        defaultSyntax.acceptedTransferSyntaxes
      );

      acceptedTransferSyntaxes.forEach((uid: string) => {
        const transferSyntaxKey = transferSyntaxMapping[uid];
        if (transferSyntaxKey && syntaxConfig[abstractSyntaxKey]) {
          (syntaxConfig[abstractSyntaxKey] as any)[transferSyntaxKey] =
            'accepted';
        }
      });
    } catch (error) {
      console.warn('Failed to parse acceptedTransferSyntaxes:', error);
    }
  });

  return syntaxConfig;
};

export const getTransferSyntaxes = (
  config: AbstractSyntaxConfig | Partial<AbstractSyntaxConfig>
) => {
  const syntaxes = [];
  const mapping = {
    implicitVrLittleEndian: '1.2.840.10008.1.2',
    explicitVrLittleEndian: '1.2.840.10008.1.2.1',
    explicitVrBigEndian: '1.2.840.10008.1.2.2',
    jpegBaselineProcess1: '1.2.840.10008.1.2.4.50',
    jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
      '1.2.840.10008.1.2.4.70',
    jpegLsLosslessImageCompression: '1.2.840.10008.1.2.4.80',
    jpegLsLossyNearLosslessImageCompression: '1.2.840.10008.1.2.4.81',
    jpeg2000ImageCompressionLosslessOnly: '1.2.840.10008.1.2.4.90',
    jpeg2000ImageCompression: '1.2.840.10008.1.2.4.91',
    mpeg4AvcH264HighProfile: '1.2.840.10008.1.2.4.102',
  };

  Object.entries(mapping).forEach(([key, uid]) => {
    if (config[key as keyof typeof config] === 'accepted') {
      syntaxes.push(uid);
    }
  });

  return syntaxes.join(',');
};


// export const transformSyntaxConfigToAPI = (syntaxConfig: DicomSyntaxConfig) => {
//   const transferCapabilities = [];
//   const services = [];



//   const verificationTransferSyntaxes = getTransferSyntaxes(
//     syntaxConfig.verificationSopClass
//   );
//   if (verificationTransferSyntaxes) {
//     transferCapabilities.push({
//       sopClass: '1.2.840.10008.1.1',
//       role: 'SCP',
//       transferSyntaxes: verificationTransferSyntaxes,
//       commonName: 'Verification SOP Class',
//       enabled: true,
//       priority: 0,
//     });
//   }

//   const secondaryCaptureTransferSyntaxes = getTransferSyntaxes(
//     syntaxConfig.secondaryCaptureImageStorage
//   );
//   if (secondaryCaptureTransferSyntaxes) {
//     transferCapabilities.push({
//       sopClass: '1.2.840.10008.5.1.4.1.1.7',
//       role: 'SCP',
//       transferSyntaxes: secondaryCaptureTransferSyntaxes,
//       commonName: 'Secondary Capture Image Storage',
//       enabled: true,
//       priority: 0,
//     });
//   }

//   const ultrasoundTransferSyntaxes = getTransferSyntaxes(
//     syntaxConfig.ultrasoundImageStorage
//   );
//   if (ultrasoundTransferSyntaxes) {
//     transferCapabilities.push({
//       sopClass: '1.2.840.10008.5.1.4.1.1.6.1',
//       role: 'SCP',
//       transferSyntaxes: ultrasoundTransferSyntaxes,
//       commonName: 'Ultrasound Image Storage',
//       enabled: true,
//       priority: 0,
//     });
//   }

//   const ultrasoundMultiFrameTransferSyntaxes = getTransferSyntaxes(
//     syntaxConfig.ultrasoundMultiFrameImageStorage
//   );
//   if (ultrasoundMultiFrameTransferSyntaxes) {
//     transferCapabilities.push({
//       sopClass: '1.2.840.10008.5.1.4.1.1.3.1',
//       role: 'SCP',
//       transferSyntaxes: ultrasoundMultiFrameTransferSyntaxes,
//       commonName: 'Ultrasound Multi-frame Image Storage',
//       enabled: true,
//       priority: 0,
//     });
//   }

//   services.push({
//     serviceType: 'C_ECHO',
//     description: 'DICOM Verification Service with auto-generated TLS',
//     enabled: true,
//     priority: 1,
//     sopClasses: [
//       {
//         sopClassUid: '1.2.840.10008.1.1',
//         sopClassName: 'Verification SOP Class',
//       },
//     ],
//   });

//   const storeSOPClasses = [];
//   if (secondaryCaptureTransferSyntaxes) {
//     storeSOPClasses.push({
//       sopClassUid: '1.2.840.10008.5.1.4.1.1.7',
//       sopClassName: 'Secondary Capture Image Storage',
//     });
//   }
//   if (ultrasoundTransferSyntaxes) {
//     storeSOPClasses.push({
//       sopClassUid: '1.2.840.10008.5.1.4.1.1.6.1',
//       sopClassName: 'Ultrasound Image Storage',
//     });
//   }
//   if (ultrasoundMultiFrameTransferSyntaxes) {
//     storeSOPClasses.push({
//       sopClassUid: '1.2.840.10008.5.1.4.1.1.3.1',
//       sopClassName: 'Ultrasound Multi-frame Image Storage',
//     });
//   }

//   if (storeSOPClasses.length > 0) {
//     services.push({
//       serviceType: 'C_STORE',
//       description: 'DICOM Storage Service with auto-generated TLS',
//       enabled: true,
//       priority: 1,
//       sopClasses: storeSOPClasses,
//     });
//   }

//   return { transferCapabilities, services };
// };


export const Badge = ({
  children,
  variant = 'default',
  className = '',
}: {
  children: React.ReactNode;
  variant?: string;
  className?: string;
}) => {
  const baseClasses =
    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
  const variantClasses = {
    default: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
    secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
    outline:
      'border border-gray-200 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800 text-title-page-color-300 dark:text-gray-300',
  };
  return (
    <span
      className={`${baseClasses} ${variantClasses[variant as keyof typeof variantClasses] || variantClasses.default} ${className}`}
    >
      {children}
    </span>
  );
};

export const Button = ({
  children,
  onClick,
  disabled = false,
  variant = 'default',
  size = 'default',
  className = '',
  type = 'button',
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: string;
  size?: string;
  className?: string;
  type?: 'button' | 'submit';
  [key: string]: unknown;
}) => {
  const baseClasses =
    'inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    default:
      'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800 focus:ring-blue-500',
    outline:
      'border border-gray-300 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800 text-title-page-color-300 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-blue-500',
    ghost:
      'text-title-page-color-300 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-blue-500',
    destructive:
      'bg-red-600 dark:bg-red-700 text-white hover:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500',
  };

  const sizeClasses = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-1.5 text-xs',
    lg: 'px-6 py-3 text-base',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant as keyof typeof variantClasses] || variantClasses.default} ${sizeClasses[size as keyof typeof sizeClasses] || sizeClasses.default} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input = ({
  className = '',
  ...props
}: {
  className?: string;
  [key: string]: unknown;
}) => {
  return (
    <input
      className={`flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800 px-3 py-2 text-sm text-title-page-color-300 dark:text-gray-300 placeholder:text-title-page-color-300 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
};

export const Select = ({
  className = '',
  children,
  value,
  onChange,
  ...props
}: {
  className?: string;
  children?: React.ReactNode;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  [key: string]: unknown;
}) => {
  return (
    <SharedSelect
      className={cn(
        'flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800 px-3 py-2 text-sm text-title-page-color-300 dark:text-gray-300 placeholder:text-title-page-color-300 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      value={value}
      onChange={onChange}
      {...props}
    >
      {children}
    </SharedSelect>
  );
};

export const Textarea = ({
  className = '',
  ...props
}: {
  className?: string;
  [key: string]: unknown;
}) => {
  return (
    <textarea
      className={`flex w-full rounded-md border border-gray-300 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800 px-3 py-2 text-sm text-title-page-color-300 dark:text-gray-300 placeholder:text-title-page-color-300 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );
};

export const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded whitespace-nowrap z-10">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
        </div>
      )}
    </div>
  );
};
